import { useEffect, useState } from "react";

import { ValueType } from "@/types/bid";
import { ProjectType } from "@/consts/Bid";
import { captureDashboardSearch } from "@/libs/dashboardSearch";
import {
  DashboardFilterType,
  GetProjectDetailsContent,
  GetProjectDetailsItem,
  GetRawContractsContent,
  GetRawContractsItem,
} from "@/types/dashboard";

import {
  useIsAdmin,
  useIsFinancialManager,
} from "@/hooks/useIsFinancialManager";

import { GetProjectDetails, GetRawContracts } from "@/services/Dashboard";

import { Skeleton } from "@/components/ui/skeleton";

import {
  DashboardDefaultProjectTotal,
  getDashboardBidFilterToString,
  getDashboardProjectFilterToString,
} from "@/consts/Dashboard";

import {
  formatKoreanCurrencyWithWon,
  formatToPercentString,
  getDate,
} from "@/libs/utils";

import { downloadExcel, getExcelFileName } from "@/utils/excelUtils";
import { showToast } from "@/utils/toastUtils";

import CommonPagination from "@/components/Common/Pagination";

const DefaultRawContractsContent: GetRawContractsContent = {
  page: 1,
  page_size: 10,
  total_count: 0,
  items: [],
};

type Props = {
  type: "bid" | "project";
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
  bidContractorIds?: ValueType[];
};
export default function DashboardContractTable({
  type,
  searchReady,
  filter,
  onSearchComplete,
  bidContractorIds,
}: Props) {
  const isProject = type === "project";
  const isFinancial = useIsFinancialManager();
  const isAdmin = useIsAdmin();
  const showSmf = isProject && (isFinancial || isAdmin);
  const pageSize = 10;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  // 프로젝트 타입용 상태
  const [tableData, setTableData] = useState<GetProjectDetailsContent>(
    DashboardDefaultProjectTotal
  );
  const [dataList, setDataList] = useState<GetProjectDetailsItem[]>([]);
  // 입찰 타입용 상태
  const [bidTableData, setBidTableData] = useState<GetRawContractsContent>(
    DefaultRawContractsContent
  );
  const [bidDataList, setBidDataList] = useState<GetRawContractsItem[]>([]);

  const handleRowClick = (id: string) => {
    if (isProject) {
      window.open(`/management/projects/${id}`);
    } else {
      window.open(`/bid/domestic/detail/${ProjectType.Original}/${id}`);
    }
  };

  const onLoadBid = async (newPage?: number) => {
    const search = captureDashboardSearch();
    try {
      setIsLoading(true);
      let filterToString = getDashboardBidFilterToString(filter);
      // bidContractorIds prop이 있으면 사용, 없으면 filter에서 가져옴
      const contractorIds = bidContractorIds ?? filter.bid_contractor_ids;
      const winBizNos = JSON.stringify(contractorIds.map((item) => item.value));
      filterToString =
        filterToString.slice(0, -1) + `, win_biz_nos=${winBizNos})`;
      const params = `${filterToString}, page=${newPage ?? page}, page_size=${pageSize}`;
      const result = await GetRawContracts(params);
      if (!search.isCurrent()) return;
      if (result.data.success) {
        setBidTableData(result.data.pods[0].content);
        setBidDataList(result.data.pods[0].content.items);
      } else {
        showToast({
          type: "error",
          message: `입찰공고 목록을 조회하는데 실패했습니다. ${result.data.error?.type}`,
        });
      }
    } catch (e) {
      if (!search.isCurrent()) return;
      showToast({
        type: "error",
        message: `입찰공고 목록을 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      if (search.isCurrent()) {
        setIsLoading(false);
        onSearchComplete();
      }
    }
  };

  const onLoadProject = async (newPage?: number) => {
    const search = captureDashboardSearch();
    try {
      setIsLoading(true);
      const filterToString = getDashboardProjectFilterToString(filter);
      const params = `${filterToString}, page=${newPage ?? page}, page_size=${pageSize}`;
      const result = await GetProjectDetails(params);
      if (!search.isCurrent()) return;
      if (result.data.success) {
        setTableData(result.data.pods[0].content);
        setDataList(result.data.pods[0].content.items);
      } else {
        showToast({
          type: "error",
          message: `프로젝트 목록을 조회하는데 실패했습니다. ${result.data.error?.type}`,
        });
      }
    } catch (e) {
      if (!search.isCurrent()) return;
      showToast({
        type: "error",
        message: `프로젝트 목록을 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      if (search.isCurrent()) {
        setIsLoading(false);
        onSearchComplete();
      }
    }
  };

  const onLoad = async (newPage?: number) => {
    if (isProject) {
      await onLoadProject(newPage);
    } else {
      await onLoadBid(newPage);
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  // 계약기업 필터 변경 시 테이블만 즉시 업데이트 (입찰공고 탭 전용)
  useEffect(() => {
    if (!isProject && bidContractorIds !== undefined) {
      setPage(1);
      onLoadBid(1);
    }
  }, [bidContractorIds]);

  const handleExcelDownload = () => {
    if (isProject) {
      if (dataList.length === 0) {
        showToast({ type: "error", message: "다운로드할 데이터가 없습니다." });
        return;
      }

      const columns = [
        { header: "입찰 공고 번호", key: "notice_no", width: 20 },
        { header: "프로젝트 명", key: "project_name", width: 30 },
        { header: "수요 기관", key: "dmin_name", width: 20 },
        { header: "공고일", key: "notice_date_display", width: 15 },
        { header: "계약일", key: "contract_date_display", width: 15 },
        { header: "계약금액", key: "bid_amount", width: 20 },
        { header: "공급기업", key: "contract_company_name", width: 20 },
        { header: "물품 수", key: "contract_count", width: 10 },
        ...(showSmf
          ? [{ header: "SMF Rate", key: "smf_rate_display", width: 15 }]
          : []),
        { header: "TPP Rate", key: "tpp_rate_display", width: 15 },
      ];

      const excelData = dataList.map((item) => ({
        ...item,
        notice_date_display: item.notice_date ? getDate(item.notice_date) : "-",
        contract_date_display: item.contract_date
          ? getDate(item.contract_date)
          : "-",
        smf_rate_display: formatToPercentString(item.smf_rate, 2),
        tpp_rate_display: formatToPercentString(item.tpp_rate, 2),
      }));

      downloadExcel(excelData, columns, getExcelFileName("최근_프로젝트_목록"));
    } else {
      if (bidDataList.length === 0) {
        showToast({ type: "error", message: "다운로드할 데이터가 없습니다." });
        return;
      }

      const columns = [
        { header: "입찰 공고 번호", key: "notice_no", width: 20 },
        { header: "공고기관", key: "agency_name", width: 20 },
        { header: "공고명", key: "project_title", width: 30 },
        { header: "업로드일", key: "upload_date_display", width: 15 },
        { header: "계약일", key: "contract_date_display", width: 15 },
        { header: "예산금액", key: "budget_amount", width: 20 },
        { header: "계약금액", key: "contract_amount", width: 20 },
        { header: "물품 수", key: "item_count", width: 10 },
        { header: "계약업체", key: "contractor_name", width: 20 },
      ];

      const excelData = bidDataList.map((item) => ({
        ...item,
        upload_date_display: getDate(item.upload_date),
        contract_date_display: getDate(item.contract_date),
      }));

      downloadExcel(excelData, columns, getExcelFileName("최근_입찰공고_목록"));
    }
  };

  // 입찰공고 테이블 렌더링
  const renderBidTable = () => (
    <>
      <table className="min-w-full text-sm">
        <thead className="bg-dark-red-60 text-white text-left text-sm font-semibold">
          <tr>
            <th className="px-4 py-2">입찰 공고 번호</th>
            <th className="px-4 py-2">공고기관</th>
            <th className="px-4 py-2">공고명</th>
            <th className="px-4 py-2">업로드일</th>
            <th className="px-4 py-2">계약일</th>
            <th className="px-4 py-2 text-right">예산금액</th>
            <th className="px-4 py-2 text-right">계약금액</th>
            <th className="px-4 py-2 text-right">물품 수</th>
            <th className="px-4 py-2">계약업체</th>
          </tr>
        </thead>
        <tbody className="divide-y text-gray-A text-xs font-normal">
          {bidDataList.map((data, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-gray-E"
              onClick={() => handleRowClick(data.project_id)}
            >
              <td className="px-4 py-2">{data.notice_no}</td>
              <td className="px-4 py-2">{data.agency_name}</td>
              <td className="px-4 py-2">{data.project_title}</td>
              <td className="px-4 py-2">{getDate(data.upload_date)}</td>
              <td className="px-4 py-2">{getDate(data.contract_date)}</td>
              <td className="px-4 py-2 text-right">
                {formatKoreanCurrencyWithWon(data.budget_amount)}
              </td>
              <td className="px-4 py-2 text-right">
                {data.contract_amount
                  ? formatKoreanCurrencyWithWon(data.contract_amount)
                  : "-"}
              </td>
              <td className="px-4 py-2 text-right">{data.item_count || "-"}</td>
              <td className="px-4 py-2">{data.contractor_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <CommonPagination
        totalItems={bidTableData.total_count}
        itemsPerPage={pageSize}
        currentPage={page}
        onPageChange={(value) => {
          setPage(value);
          onLoad(value);
        }}
      />
    </>
  );

  // 프로젝트 테이블 렌더링
  const renderProjectTable = () => (
    <>
      <table className="min-w-full text-sm">
        <thead className="bg-dark-red-60 text-white text-left text-sm font-semibold">
          <tr>
            <th className="px-4 py-2">입찰 공고 번호</th>
            <th className="px-4 py-2">프로젝트 명</th>
            <th className="px-4 py-2">수요 기관</th>
            <th className="px-4 py-2">공고일</th>
            <th className="px-4 py-2">계약일</th>
            <th className="px-4 py-2 text-right">계약금액</th>
            <th className="px-4 py-2">공급기업</th>
            <th className="px-4 py-2 text-right">물품 수</th>
            {showSmf && <th className="px-4 py-2 text-right">SMF Rate</th>}
            {showSmf && <th className="px-4 py-2 text-right">SMF Margin</th>}
            <th className="px-4 py-2 text-right">TPP Rate</th>
            <th className="px-4 py-2 text-right">TPP Margin</th>
          </tr>
        </thead>
        <tbody className="divide-y text-gray-A text-xs font-normal">
          {dataList.map((data, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-gray-E"
              onClick={() => handleRowClick(data.project_group_id)}
            >
              <td className="px-4 py-2">{data.notice_no}</td>
              <td className="px-4 py-2">{data.project_name}</td>
              <td className="px-4 py-2">{data.dmin_name}</td>
              <td className="px-4 py-2">
                {data.notice_date ? getDate(data.notice_date) : "-"}
              </td>
              <td className="px-4 py-2">
                {data.contract_date ? getDate(data.contract_date) : "-"}
              </td>
              <td className="px-4 py-2 text-right">
                {data.bid_amount
                  ? formatKoreanCurrencyWithWon(data.bid_amount)
                  : "-"}
              </td>
              <td className="px-4 py-2">{data.contract_company_name}</td>
              <td className="px-4 py-2 text-right">
                {data.contract_count || "-"}
              </td>
              {showSmf && (
                <td className="px-4 py-2 text-right">
                  {formatToPercentString(data.smf_rate, 2)}
                </td>
              )}
              {showSmf && (
                <td className="px-4 py-2 text-right">
                  {formatToPercentString(data.smf_margin, 2)}
                </td>
              )}
              <td className="px-4 py-2 text-right">
                {formatToPercentString(data.tpp_rate, 2)}
              </td>
              <td className="px-4 py-2 text-right">
                {formatToPercentString(data.tpp_margin, 2)}
              </td>
            </tr>
          ))}

          <tr className="text-gray-A text-sm font-semibold">
            <td colSpan={3} className="px-4 py-2">
              합계
            </td>
            <td className="px-4 py-2 text-right">
              {formatKoreanCurrencyWithWon(tableData.total_bid_amount)}
            </td>
            <td></td>
            <td className="px-4 py-2 text-right">
              {tableData.total_project_count.toLocaleString()}
            </td>
            {showSmf && (
              <td className="px-4 py-2 text-right">
                {formatToPercentString(tableData.total_smf_rate)}
              </td>
            )}
            <td className="px-4 py-2 text-right">
              {formatToPercentString(tableData.total_tpp_rate)}
            </td>
          </tr>
        </tbody>
      </table>
      <CommonPagination
        totalItems={tableData.total_count}
        itemsPerPage={DashboardDefaultProjectTotal.page_size}
        currentPage={page}
        onPageChange={(value) => {
          setPage(value);
          onLoad(value);
        }}
      />
    </>
  );

  const renderTableSkeleton = () => (
    <div className="p-4 space-y-3">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex gap-4">
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="h-14 px-5 py-4 bg-white rounded-t-xl outline outline-1 outline-gray-D flex justify-between items-center text-gray-A text-sm font-semibold">
        <div className="flex items-center gap-4">
          <span>{`최근 ${isProject ? "프로젝트" : "입찰공고"} 목록`}</span>
          {!isProject &&
            (bidContractorIds ?? filter.bid_contractor_ids).length > 0 && (
              <span className="text-xs font-normal text-gray-500">
                계약기업 필터 적용됨
              </span>
            )}
        </div>
        <button
          className="flex items-center gap-1 text-xs text-gray-A hover:text-gray-600"
          onClick={handleExcelDownload}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.66666 6.66669L7.99999 10L11.3333 6.66669"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 10V2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          EXCEL 다운로드
        </button>
      </div>

      <div className="pb-4 overflow-x-auto">
        {isLoading
          ? renderTableSkeleton()
          : isProject
            ? renderProjectTable()
            : renderBidTable()}
      </div>
    </div>
  );
}
